import { getSupabase } from './supabase';

/**
 * API client (E3.6 scope used by E2): injects `Authorization: Bearer <access_jwt>`
 * and applies the standard error contract. The token is read
 * fresh from the Supabase SDK on each call so auto-refreshed tokens are used.
 *
 * V1 endpoints live under /api/v1; base is configurable for the tunnel deploy.
 */
// 서버(SSR loader)는 같은 호스트의 API 를 루프백으로 부른다 — server/fetch.ts 와 같은 값.
// 빈 문자열 env 도 없는 것으로 친다(`??` 가 아니라 `||`).
const API_BASE = import.meta.env.SSR
  ? process.env.SSR_API_BASE_URL || process.env.VITE_API_BASE_URL || 'http://127.0.0.1:8011/api/v1'
  : import.meta.env.VITE_API_BASE_URL || '/api/v1';

/** Standard API error carrying the HTTP status (status-code contract). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 401 — caller should drive the login flow (AC-1.2). */
export class UnauthorizedError extends ApiError {
  constructor(message = 'Authentication required') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

async function authHeader(): Promise<Record<string, string>> {
  // 서버는 항상 비로그인으로 그린다(docs/SSR.md 「인증」). 세션은 브라우저에만 있다.
  if (import.meta.env.SSR) return {};
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeader()),
      ...init?.headers,
    },
  });

  if (res.status === 401) {
    throw new UnauthorizedError();
  }

  if (!res.ok) {
    // API errors are { code, message }; fall back to status text.
    const message = await res
      .json()
      .then((b: { message?: string }) => b?.message)
      .catch(() => undefined);
    throw new ApiError(res.status, message ?? res.statusText);
  }

  // 204 No Content has no body.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
